import { useTranslation } from 'react-i18next';
import { AlertTriangle, MapPin } from 'lucide-react';
import type { EstimateResponse } from '$lib/api/estimate';
import { formatKr, formatKrPerSqm, formatSqm, formatYear } from '$lib/lib/formatters';

// EstimateResult — operator-focused display of the address-driven estimate.
//
// Three sections, top → bottom:
//   1. The identified Unit profile (m² · rooms · year · energy · floor/door)
//   2. The price band: low | suggested | high (kr/month)
//   3. A confidence chip — sample size + thin-sample warning when n<15
//
// No marketing copy, no animations. The output is "what to ask" — the
// suggested figure is the median of comparables × area, framed by p25/p75.

export interface EstimateResultProps {
  data: EstimateResponse;
}

export function EstimateResult({ data }: EstimateResultProps) {
  const { t } = useTranslation();
  const { unit, resolved, estimate } = data;

  const addressLine = [resolved.address, resolved.postal_code, resolved.city]
    .filter((s) => s && s.length > 0)
    .join(' ');
  const floorLine = [unit.floor, unit.door].filter(Boolean).join('. ');

  return (
    <section
      data-testid="estimate-result"
      className="flex flex-col gap-3"
      aria-label={t('estimate.resultAriaLabel')}
    >
      {/* --- Identified Unit -------------------------------------------- */}
      <div
        data-testid="estimate-unit-profile"
        className="rounded-sm border border-rule bg-surface px-3 py-2"
      >
        <div className="flex items-baseline gap-2">
          <MapPin size={14} className="text-fg-muted" />
          <span className="text-sm text-fg" data-testid="estimate-address">
            {addressLine || '—'}
            {floorLine && (
              <span className="ml-1 text-fg-muted">, {floorLine}</span>
            )}
          </span>
        </div>
        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-2xs">
          <ProfileFact label={t('estimate.profile.area')} value={formatSqm(unit.area_m2)} />
          <ProfileFact
            label={t('estimate.profile.rooms')}
            value={unit.rooms != null ? String(unit.rooms) : '—'}
          />
          <ProfileFact label={t('estimate.profile.year')} value={formatYear(unit.construction_year)} />
          <ProfileFact label={t('estimate.profile.energy')} value={unit.energy_label ?? '—'} />
        </dl>
      </div>

      {/* --- Estimate band ---------------------------------------------- */}
      <div
        data-testid="estimate-band"
        className="rounded-sm border border-rule bg-surface px-3 py-3"
      >
        <div className="grid grid-cols-3 gap-3">
          <BandSlot
            tone="muted"
            label={t('estimate.band.low')}
            kr={estimate.low_kr_per_month}
            krPerSqm={estimate.low_kr_per_sqm}
            testId="estimate-low"
          />
          <BandSlot
            tone="primary"
            label={t('estimate.band.suggested')}
            kr={estimate.suggested_kr_per_month}
            krPerSqm={estimate.suggested_kr_per_sqm}
            testId="estimate-suggested"
          />
          <BandSlot
            tone="muted"
            label={t('estimate.band.high')}
            kr={estimate.high_kr_per_month}
            krPerSqm={estimate.high_kr_per_sqm}
            testId="estimate-high"
          />
        </div>
        {estimate.low_kr_per_month == null && (
          <p
            data-testid="estimate-no-numbers"
            className="mt-2 text-2xs text-fg-muted"
          >
            {t('estimate.noNumbers')}
          </p>
        )}
      </div>

      {/* --- Confidence chip -------------------------------------------- */}
      <div data-testid="estimate-confidence" className="flex items-center gap-2 text-2xs">
        {estimate.comparables_count === 0 ? (
          <span
            data-testid="estimate-empty"
            className="inline-flex items-center gap-1 rounded-xs border border-warning/35 bg-warning/15 px-1.5 py-0.5 text-warning"
          >
            <AlertTriangle size={12} />
            {t('estimate.confidence.empty')}
          </span>
        ) : estimate.thin_sample ? (
          <span
            data-testid="estimate-thin"
            className="inline-flex items-center gap-1 rounded-xs border border-warning/35 bg-warning/15 px-1.5 py-0.5 text-warning"
          >
            <AlertTriangle size={12} />
            {t('estimate.confidence.thin', { count: estimate.comparables_count })}
          </span>
        ) : (
          <span
            data-testid="estimate-healthy"
            className="inline-flex items-center gap-1 rounded-xs border border-rule bg-surface-2 px-1.5 py-0.5 text-fg-muted"
          >
            {t('estimate.confidence.healthy', { count: estimate.comparables_count })}
          </span>
        )}
      </div>
    </section>
  );
}

interface ProfileFactProps {
  label: string;
  value: React.ReactNode;
}
function ProfileFact({ label, value }: ProfileFactProps) {
  return (
    <div className="flex items-baseline gap-1">
      <dt className="uppercase tracking-wider text-fg-subtle">{label}</dt>
      <dd className="num text-fg">{value ?? '—'}</dd>
    </div>
  );
}

interface BandSlotProps {
  tone: 'muted' | 'primary';
  label: string;
  kr: number | null;
  krPerSqm: number | null;
  testId: string;
}
function BandSlot({ tone, label, kr, krPerSqm, testId }: BandSlotProps) {
  const labelTone = tone === 'primary' ? 'text-accent' : 'text-fg-subtle';
  const krTone = tone === 'primary' ? 'text-fg text-lg font-semibold' : 'text-fg text-sm';
  return (
    <div data-testid={testId} className="flex flex-col gap-0.5">
      <span className={`text-2xs uppercase tracking-wider ${labelTone}`}>{label}</span>
      <span className={`num ${krTone}`}>{formatKr(kr)}</span>
      <span className="num text-2xs text-fg-muted">{formatKrPerSqm(krPerSqm)}</span>
    </div>
  );
}
